/**
 * @license
 * Copyright 2026 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 *
 * g3-prettier-ignore-file
 */

/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { GoogleGenAICore } from "../core.js";
import { encodeJSON, encodeSimple } from "../lib/encodings.js";
import { matchStatusCode } from "../lib/http.js";
import * as M from "../lib/matchers.js";
import { compactMap } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { extractSecurity, resolveGlobalSecurity } from "../lib/security.js";
import { pathToFunc } from "../lib/url.js";
import { GoogleGenAiError } from "../models/errors/google-gen-ai-error.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/http-client-errors.js";
import * as errors from "../models/errors/index.js";
import * as operations from "../models/operations/index.js";
import { APICall, APIPromise } from "../types/async.js";
import { Result } from "../types/fp.js";

/**
 * Creating an interaction
 *
 * @remarks
 * Creates a new interaction.
 */
export function interactionsCreate(
  client: GoogleGenAICore,
  body: operations.CreateInteractionRequestBody & { stream?: false },
  api_version?: string | undefined,
  options?: RequestOptions,
): APIPromise<
  Result<
    operations.CreateInteractionResponse,
    | errors.CreateInteractionClientError
    | errors.CreateInteractionServerError
    | GoogleGenAiError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
  >
>;
export function interactionsCreate(
  client: GoogleGenAICore,
  body: operations.CreateInteractionRequestBody & { stream: true },
  api_version?: string | undefined,
  options?: RequestOptions,
): APIPromise<
  Result<
    operations.CreateInteractionResponse,
    | errors.CreateInteractionClientError
    | errors.CreateInteractionServerError
    | GoogleGenAiError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
  >
>;
export function interactionsCreate(
  client: GoogleGenAICore,
  body: operations.CreateInteractionRequestBody,
  api_version?: string | undefined,
  options?: RequestOptions,
): APIPromise<
  Result<
    operations.CreateInteractionResponse,
    | errors.CreateInteractionClientError
    | errors.CreateInteractionServerError
    | GoogleGenAiError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
  >
>;
export function interactionsCreate(
  client: GoogleGenAICore,
  body: operations.CreateInteractionRequestBody,
  api_version?: string | undefined,
  options?: RequestOptions,
): APIPromise<
  Result<
    operations.CreateInteractionResponse,
    | errors.CreateInteractionClientError
    | errors.CreateInteractionServerError
    | GoogleGenAiError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
  >
> {
  return new APIPromise($do(
    client,
    body,
    api_version,
    options,
  ));
}

async function $do(
  client: GoogleGenAICore,
  body: operations.CreateInteractionRequestBody,
  api_version?: string | undefined,
  options?: RequestOptions,
): Promise<
  [
    Result<
      operations.CreateInteractionResponse,
      | errors.CreateInteractionClientError
      | errors.CreateInteractionServerError
      | GoogleGenAiError
      | ConnectionError
      | RequestAbortedError
      | RequestTimeoutError
      | InvalidRequestError
      | UnexpectedClientError
    >,
    APICall,
  ]
> {
  const input: operations.CreateInteractionRequest = {
    body: body,
    api_version: api_version,
  };

  const payload = input;
  const body$ = encodeJSON("body", payload.body, { explode: true });

  const pathParams = {
    api_version: encodeSimple(
      "api_version",
      payload.api_version ?? client._options.api_version,
      { explode: false, charEncoding: "percent" },
    ),
  };
  const path = pathToFunc("/{api_version}/interactions")(pathParams);

  const headers = new Headers(compactMap({
    "Content-Type": "application/json",
    Accept: input?.body?.stream ? "text/event-stream" : "application/json",
  }));

  const securityInput = await extractSecurity(client._options.security);
  const requestSecurity = resolveGlobalSecurity(securityInput);

  const context = {
    options: client._options,
    base_url: options?.server_url ?? client._baseURL ?? "",
    operation_id: "CreateInteraction",
    o_auth2_scopes: null,

    resolved_security: requestSecurity,

    security_source: client._options.security,
    retry_config: options?.retries
      || client._options.retry_config
      || {
        strategy: "attempt-count-backoff",
        backoff: {
          initialInterval: 500,
          maxInterval: 8000,
          exponent: 2,
          maxElapsedTime: 30000,
        },
        retryConnectionErrors: true,
        maxRetries: 4,
      }
      || { strategy: "none" },
    retry_codes: options?.retry_codes || ["408", "409", "429", "5XX"],
  };

  const requestRes = client._createRequest(context, {
    security: requestSecurity,
    method: "POST",
    baseURL: options?.server_url,
    path: path,
    headers: headers,
    body: body$,
    userAgent: client._options.user_agent,
    timeout_ms: options?.timeout_ms || client._options.timeout_ms || -1,
  }, options);
  if (!requestRes.ok) {
    return [requestRes, { status: "invalid" }];
  }
  const req = requestRes.value;

  const doResult = await client._do(req, {
    context,
    isErrorStatusCode: (statusCode: number) =>
      matchStatusCode({ status: statusCode } as Response, ["4XX", "5XX"]),
    retryConfig: context.retry_config,
    retryCodes: context.retry_codes,
  });
  if (!doResult.ok) {
    return [doResult, { status: "request-error", request: req }];
  }
  const response = doResult.value;

  const responseFields = {
    httpMeta: { response: response, request: req },
  };

  const [result] = await M.match<
    operations.CreateInteractionResponse,
    | errors.CreateInteractionClientError
    | errors.CreateInteractionServerError
    | GoogleGenAiError
    | ConnectionError
    | RequestAbortedError
    | RequestTimeoutError
    | InvalidRequestError
    | UnexpectedClientError
  >(
    M.json<operations.CreateInteractionResponse>(200),
    M.sse<operations.CreateInteractionResponse>(200, {
      sentinel: "[DONE]",
      flattened: true,
    }),
    M.jsonErr<errors.CreateInteractionClientError>(
      "4XX",
      errors.CreateInteractionClientError,
    ),
    M.jsonErr<errors.CreateInteractionServerError>(
      "5XX",
      errors.CreateInteractionServerError,
    ),
  )(response, req, { extraFields: responseFields });
  if (!result.ok) {
    return [result, { status: "complete", request: req, response }];
  }

  return [result, { status: "complete", request: req, response }];
}
